// Licence expiry conversion and state — ATC-129 / `ATC-121`.
//
// Expiry is a SECOND, INDEPENDENT AXIS from status (`ATC-ADR-011`'s R-ATC-10
// amendment). An environment can be `active` AND expired: `Get-Entitlements`
// refuses it with `token_expired` while every list endpoint still reports a
// green `active`. The console must therefore surface expiry DISTINCTLY rather
// than folding it into the status chip — a green chip on a dead credential is
// exactly the benign-looking terminal state `ATC-ADR-018` §1.3 exists to
// prevent.

/** Days before expiry at which a licence starts reading as expiring. */
export const EXPIRING_WINDOW_DAYS = 14

const MS_PER_DAY = 86_400_000

export type ExpiryState = 'none' | 'active' | 'expiring' | 'expired'

/**
 * `now` is INJECTED, never read from the clock.
 *
 * A pure function with an explicit instant needs no fake timers, is trivially
 * testable at a boundary, and cannot produce a test that passes in one timezone
 * and fails in another. The caller passes `Date.now()`.
 */
export function expiryState(expiresAt: string | null | undefined, now: number): ExpiryState {
  if (!expiresAt) {
    return 'none'
  }
  const at = Date.parse(expiresAt)
  if (Number.isNaN(at)) {
    // An unparseable value is NOT treated as "no expiry". `Get-Entitlements`
    // fails OPEN on an unparseable `expiresAt` — it never expires the token —
    // so a value the console cannot read is a value that is silently
    // unenforced. Surfacing it as `expiring` puts it in front of an operator
    // instead of rendering it as perpetual.
    return 'expiring'
  }
  if (at <= now) {
    return 'expired'
  }
  if (at - now <= EXPIRING_WINDOW_DAYS * MS_PER_DAY) {
    return 'expiring'
  }
  return 'active'
}

/**
 * Convert a native `<input type="date">` value (`YYYY-MM-DD`) to the canonical
 * instant the server stores.
 *
 * END OF DAY, not midnight, and this is the whole decision. A licence bought
 * "until the 31st" must still work ON the 31st; midnight-on-the-31st kills it
 * a day early, at the start of the day the customer paid for. The server
 * refuses a bare date precisely because either convention is a silent 24-hour
 * discrepancy on a kill switch, so the console picks one explicitly and says so.
 *
 * Returns `null` for anything that is not a well-formed date, so a caller
 * cannot forward garbage into a `PATCH`.
 */
export function dateInputToIso(dateInput: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return null
  }
  const at = Date.parse(`${dateInput}T23:59:59.999Z`)
  if (Number.isNaN(at)) {
    return null
  }
  // Round-trip through Date so an impossible date (2026-02-30) is rejected
  // rather than silently rolled forward by the parser.
  const iso = new Date(at).toISOString()
  if (!iso.startsWith(dateInput)) {
    return null
  }
  // The server's accepted format is `yyyy-MM-ddTHH:mm:ss.fffZ`; toISOString
  // emits exactly that.
  return iso
}

/**
 * The inverse, for pre-filling a date input from a stored instant. Returns ''
 * for absent or unparseable values so the input renders empty rather than
 * `Invalid Date`.
 */
export function isoToDateInput(expiresAt: string | null | undefined): string {
  if (!expiresAt) {
    return ''
  }
  const at = Date.parse(expiresAt)
  if (Number.isNaN(at)) {
    return ''
  }
  return new Date(at).toISOString().slice(0, 10)
}
